import { useState, useEffect } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import dayjs from 'dayjs';
import RewardPointsAndBonusContainer from '../../container/rewardPointsAndBonusContainer';
import './style.scss';
import ViewRewardModal from '../../../RnRCommonComponent/ViewRewardModal/ViewRewardModal.jsx';
import RecognitionReward from 'src/_shared/components/recognition-reward/RecognitionReward.jsx';
import RecentActivityCard from '../recentActivityCard/RecentActivityCard.jsx';
import MyBadgesList from '../../components/MyBadgesList/MyBadgesList.jsx';
import useMyBadgeAssignments from '../../hooks/useMyBadgeAssignments';
import MySentActivityComponent from 'src/my-rewards/components/MySentActivityComponent.jsx';
import { QueryPointsBalancesByUserId } from 'src/_shared/api/graphql/custom/dashboard/queryPointsBalancesByUserIdGraphql.js';
import { GetPointsSettings } from 'src/new-points/pointsSettingsHelpers.js';
import { Typography, Card, Row, Col } from 'antd';
import {
	MilestoneGiftBanner,
	MilestoneRewardGroup,
} from '../../components/MilestoneClaims/MilestoneClaims.jsx';
import {
	countOutstanding,
	isGiftOutstanding,
	isPinOutstanding,
	needsAttention,
} from 'src/_shared/components/milestone-gift-modal/milestoneClaims.js';
import { isBackfilledAssignment } from 'src/rewardsAndRecognitionHelper/utils.js';

const { Title } = Typography;
const QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID = gql`
	query QuerySnappyGiftsByUserIdCompanyId($userId: ID!, $companyId: ID!) {
		querySnappyGiftsByUserIdCompanyId(userId: $userId, companyId: $companyId) {
			id
			companyId
			itemType
			userId
			badgeAssignmentId
			badgeAssignment {
				id
				badgeId
				badge {
					name
					id
				}
			}
			link
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			firstSeenAt
			createdAt
			claimedOn
		}
	}
`;

const QUERY_RECOGNITION_LEADERBOARD_ENTRIES = gql`
	query QueryRecognitionLeaderboardEntries(
		$companyId: ID!
		$userId: ID!
		$subCompanyId: ID
	) {
		queryRecognitionLeaderboardEntries(
			companyId: $companyId
			userId: $userId
			subCompanyId: $subCompanyId
		) {
			fullName
			isUser
			position
			totalPoints
		}
	}
`;

const MyrewardsPresentation = (props) => {
	const {
		handleOpenViewReward,
		handleCloseViewReward,
		isViewRewardModalVisible,
		rewardDetails,
		isModalLoaderVisible,
		currentUser,
		testRecognition,
		disableSpotlight,
		recentActivities = [],
		badgeAssignments = [],
		onClaimGift,
		onViewRecognition,
		onViewBadgeDetails,
		onMarkAsSeen,
		onClearTestRecognition,
		processingGiftClaims = new Set(),
		isLoadingActivities = false,
		milestoneClaims = [],
		processingMilestoneClaims = new Set(),
		onClaimMilestonePin,
		onClaimMilestoneGift,
	} = props;
	const disableSentActivity = currentUser?.featureFlags?.includes(
		'disableSentActivity'
	);
	const [shouldRefresh, setShouldRefresh] = useState(false);
	const [snappyGifts, setSnappyGifts] = useState([]);
	const [userBadgePoints, setUserBadgePoints] = useState(0);
	const apolloClient = useApolloClient();
	const newPointsEarlyPreview = currentUser?.featureFlags?.includes(
		'newPointsEarlyPreview'
	);
	const isSharingVisible =
		!currentUser?.company?.recognitionSettings?.hideRecognitionSocialShare;

	const tiers =
		currentUser?.company?.recognitionSettings?.tieredRewardsCampaign || [];

	const {
		isLoading,
		badgeList = [],
		hasMore,
		fetchMoreData,
	} = useMyBadgeAssignments(currentUser, shouldRefresh, setShouldRefresh);
	const milestoneClaimsById = new Map(
		milestoneClaims.map((claim) => [claim.id, claim])
	);
	const openMilestoneClaims = milestoneClaims.filter(needsAttention);
	const milestoneGiftClaims = milestoneClaims.filter(isGiftOutstanding);

	const getMilestoneClaimTag = (assignmentId) => {
		const claim = milestoneClaimsById.get(assignmentId);
		if (!claim || !needsAttention(claim)) return null;
		if (isGiftOutstanding(claim) && isPinOutstanding(claim)) {
			return '2 rewards to claim';
		}
		return isGiftOutstanding(claim) ? 'Gift to claim' : 'Pin to claim';
	};

	const assignmentsWithBadges = badgeList
		.filter((assignment) => assignment?.badgeId)
		.map((assignment) => {
			const claimTag = getMilestoneClaimTag(assignment.assignmentId);
			return claimTag ? { ...assignment, claimTag } : assignment;
		});
	const userBadgeCount = assignmentsWithBadges?.length || 0;

	const querySnappyGifts = async () => {
		if (!currentUser?.id || !currentUser?.company?.id) {
			return;
		}

		try {
			const { data } = await apolloClient.query({
				query: QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID,
				variables: {
					userId: currentUser.id,
					companyId: currentUser.company.id,
				},
				fetchPolicy: 'network-only',
			});

			setSnappyGifts(data?.querySnappyGiftsByUserIdCompanyId || []);
		} catch (error) {
			console.error('Error querying snappy gifts:', error);
		}
	};

	const queryLeaderboardPoints = async () => {
		if (!currentUser?.id || !currentUser?.company?.id) {
			return;
		}

		try {
			const { data } = await apolloClient.query({
				query: QUERY_RECOGNITION_LEADERBOARD_ENTRIES,
				variables: {
					userId: currentUser.id,
					companyId: currentUser.company.id,
				},
				fetchPolicy: 'network-only',
			});

			const userEntry = data?.queryRecognitionLeaderboardEntries?.find(
				(entry) => entry.isUser === true
			);

			if (userEntry) {
				setUserBadgePoints(userEntry.totalPoints || 0);
			}
		} catch (error) {
			console.error('Error querying leaderboard entries:', error);
		}
	};

	const getCurrentPeriodKey = (primaryPeriod) => {
		const now = dayjs();
		switch (primaryPeriod) {
			case 'YEARLY':
				return now.format('YYYY');
			case 'QUARTERLY': {
				const quarter = Math.ceil((now.month() + 1) / 3);
				return `${now.format('YYYY')}-Q${quarter}`;
			}
			case 'MONTHLY':
				return now.format('YYYY-MM');
			case 'ALLTIME':
			default:
				return null;
		}
	};

	const queryPointsBalance = async () => {
		if (!currentUser?.id || !currentUser?.companyId) return;

		try {
			const [balancesResponse, settingsResponse] = await Promise.all([
				apolloClient.query({
					query: QueryPointsBalancesByUserId,
					variables: { userId: currentUser.id, first: 100 },
					fetchPolicy: 'network-only',
				}),
				apolloClient.query({
					query: GetPointsSettings,
					variables: { companyId: currentUser.companyId },
					fetchPolicy: 'network-only',
				}),
			]);

			const balances = (
				balancesResponse?.data?.queryPointsBalancesByUserId?.items ?? []
			).filter((b) => b.companyId === currentUser.companyId);
			const settings = settingsResponse?.data?.getPointsSettings;
			const primaryPeriod = settings?.primaryPeriod ?? 'ALLTIME';
			const excludeRecognitionPointsFromReset =
				settings?.excludeRecognitionPointsFromReset ?? false;

			let matchedBalance;
			if (primaryPeriod === 'ALLTIME' || excludeRecognitionPointsFromReset) {
				matchedBalance = balances.find((b) => b.isAllTime);
			} else {
				const periodKey = getCurrentPeriodKey(primaryPeriod);
				matchedBalance = balances.find((b) => b.periodKey === periodKey);
			}

			setUserBadgePoints(matchedBalance?.pointsRecognition ?? 0);
		} catch (error) {
			console.error('Error querying points balances:', error);
		}
	};

	useEffect(() => {
		querySnappyGifts();
		if (newPointsEarlyPreview) {
			queryPointsBalance();
		} else {
			queryLeaderboardPoints();
		}
	}, [currentUser?.id, currentUser?.company?.id, processingGiftClaims]);

	const createTierClaimActivities = () => {
		if (!tiers || tiers.length === 0) return [];

		return tiers
			.filter((tier) => {
				const isClaimable =
					tier.unlockConditionType === 'pointsEarned'
						? userBadgePoints >= tier.unlockConditionAmount
						: userBadgeCount >= tier.unlockConditionAmount;
				return isClaimable;
			})
			.map((tier) => {
				const existingSnappyGift = snappyGifts.find(
					(snappyGift) => snappyGift.snappyCampaignTierId === tier.id
				);

				const hasSnappyLink = !!existingSnappyGift?.link;
				const hasGiftLink = !!tier.gift?.giftDetails?.link;
				const hasAnyLink = hasSnappyLink || hasGiftLink;

				const giftStatus = tier.gift?.giftDetails?.status;
				const isAlreadyClaimed = giftStatus === 'claimed';

				if (isAlreadyClaimed) {
					return null;
				}

				return {
					id: `tier-claim-${tier.id}`,
					type: 'tier_claim',
					tierName: tier.name,
					tierDescription: tier.description,
					tier: tier,
					existingSnappyGift: existingSnappyGift,
					title: `Tier "${tier.name}" is Unlocked`,
					description: tier.description || 'Tier reward is ready to claim',
					createdAt: new Date().toISOString(),
					isNew: !hasAnyLink,
					hasSeen: false,
					giftStatus: giftStatus,
					hasLink: hasAnyLink,
					hasSnappyLink: hasSnappyLink,
					hasGiftLink: hasGiftLink,
					isProcessing: processingGiftClaims.has(tier.id),
				};
			})
			.filter(Boolean);
	};

	const createAutoApprovedGiftActivities = () => {
		if (!badgeList || badgeList.length === 0) return [];
		const mappedAssignments = badgeList
			.filter((badgeAssignment) => {
				const isClaimable =
					badgeAssignment?.snappyGrant?.externalSnappyId &&
					badgeAssignment?.snappyGrant?.id &&
					!badgeAssignment?.sentWithoutSnappyGrant &&
					!isBackfilledAssignment(badgeAssignment) &&
					badgeAssignment.assignmentId &&
					badgeAssignment.approvalStatus === 'Approved';
				return isClaimable;
			})
			.map((badgeAssignment) => {
				const existingSnappyGift = snappyGifts.find(
					(snappyGift) =>
						snappyGift.badgeAssignmentId === badgeAssignment.assignmentId
				);
				if (existingSnappyGift) {
					return null;
				}

				return {
					id: `badge-assignment-${badgeAssignment.assignmentId}`,
					type: 'gift_uncreated',
					badgeName: badgeAssignment.name,
					badgeDescription: badgeAssignment.description,
					badgeAssignment: badgeAssignment.rawAssignment,
					badgeAssignmentId: badgeAssignment.assignmentId,
					companyId: badgeAssignment.badge.companyId,
					createdAt: badgeAssignment.createdAt,
					externalSnappyGiftId: badgeAssignment.snappyGrant.externalSnappyId,
					snappyGrantId: badgeAssignment.snappyGrant.id,
					userId: badgeAssignment.userId,
					isNew: false,
					hasSeen: false,
					link: null,
					giftStatus: 'uncreated',
					isProcessing: processingGiftClaims.has(badgeAssignment.assignmentId),
				};
			})
			.filter(Boolean);
		return mappedAssignments;
	};

	const createCombinedActivities = () => {
		const tierActivities = createTierClaimActivities();
		const autoApprovedGiftActivities = createAutoApprovedGiftActivities();
		const allActivities = [
			...recentActivities,
			...tierActivities,
			...autoApprovedGiftActivities,
		];
		return allActivities.sort(
			(a, b) => new Date(b.createdAt) - new Date(a.createdAt)
		);
	};

	// Gifts on milestones with a pin and a gift are shown in their own group
	const isGroupedMilestoneGift = (activity) => {
		const assignmentId =
			activity.badgeAssignmentId || activity.rawData?.gift?.badgeAssignmentId;
		return Boolean(assignmentId && milestoneClaimsById.has(assignmentId));
	};

	const combinedActivities = createCombinedActivities().filter(
		(activity) => !isGroupedMilestoneGift(activity)
	);

	const rewardsToClaimCount =
		openMilestoneClaims.reduce(
			(total, claim) => total + countOutstanding(claim),
			0
		) +
		combinedActivities.filter(
			(activity) =>
				activity.type === 'gift_uncreated' ||
				(activity.type === 'gift_received' && activity.giftStatus !== 'expired')
		).length;

	const milestoneGroups =
		openMilestoneClaims.length > 0
			? openMilestoneClaims.map((claim) => (
					<MilestoneRewardGroup
						key={claim.id}
						claim={claim}
						onClaimPin={onClaimMilestonePin}
						onClaimGift={onClaimMilestoneGift}
						processingKeys={processingMilestoneClaims}
					/>
				))
			: null;

	return (
		<div className="my-recognitions">
			<div className="my-recognitions-container" style={{ marginBottom: 0 }}>
				<Title
					level={2}
					style={{
						margin: 0,
						fontWeight: 700,
						fontSize: '30px',
						marginBottom: 12,
					}}
				>
					My Recognitions & Rewards
				</Title>
				{milestoneGiftClaims.map((claim) => (
					<MilestoneGiftBanner
						key={claim.id}
						claim={claim}
						onClaimGift={onClaimMilestoneGift}
						isProcessing={processingMilestoneClaims.has(`${claim.id}:gift`)}
					/>
				))}
				<Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
					<Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
						<RewardPointsAndBonusContainer
							userBadgePoints={userBadgePoints}
							userBadgeCount={userBadgeCount}
							rewardsToClaimCount={rewardsToClaimCount}
						/>
					</Col>
					<Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
						<Card
							style={{
								border: '1px solid #dddddd',
								borderRadius: '14px',
							}}
							bodyStyle={{ padding: '16px' }}
						>
							<div>
								<h4
									style={{
										fontSize: '14px',
										lineHeight: '18px',
										fontWeight: 700,
										margin: '0',
										color: 'var(--dove-gray)',
									}}
								>
									Recognitions Rewards
								</h4>
							</div>
							<RecentActivityCard
								recentActivities={combinedActivities}
								onClaimGift={onClaimGift}
								onViewRecognition={onViewRecognition}
								onMarkAsSeen={onMarkAsSeen}
								disableSpotlight={disableSpotlight}
								isLoading={isLoadingActivities}
								isCombinedMode={true}
								pinnedContent={milestoneGroups}
							/>
						</Card>
					</Col>
				</Row>
			</div>

			{testRecognition && (
				<div className="test-recognition-container">
					<RecognitionReward
						recognition={testRecognition}
						currentUser={currentUser}
						disableSpotlight={disableSpotlight}
						onStateChange={(newState, id) => {
							console.log(`Recognition ${id} state changed to ${newState}`);
							if (newState === 'viewed' && onClearTestRecognition) {
								onClearTestRecognition();
							}
						}}
						onClaimGift={onClaimGift}
					/>
				</div>
			)}
			<Row gutter={[16, 16]}>
				<Col
					xs={24}
					sm={24}
					md={24}
					lg={24}
					xl={disableSentActivity ? 24 : 12}
					xxl={disableSentActivity ? 24 : 12}
				>
					<MyBadgesList
						handleIconClickEvent={(badge) => {
							onViewBadgeDetails(badge);
						}}
						title="My Recognitions"
						shouldRefresh={shouldRefresh}
						setShouldRefresh={setShouldRefresh}
						badgeList={assignmentsWithBadges}
						isLoading={isLoading}
						hasMore={hasMore}
						fetchMoreData={fetchMoreData}
						isDetailsShown={false}
						isPointsShown={true}
						isIssuedVisible={true}
						currentUser={currentUser}
						client={apolloClient}
						noCollapse={disableSentActivity ? false : true}
					/>
				</Col>
				{!disableSentActivity && (
					<Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
						<Card
							style={{
								border: '1px solid #dddddd',
								borderRadius: '14px',
							}}
							bodyStyle={{ padding: '16px' }}
						>
							<div>
								<h4
									style={{
										fontSize: '14px',
										lineHeight: '18px',
										fontWeight: 700,
										margin: '0 0 14px',
										color: 'var(--dove-gray)',
									}}
								>
									My Sent Activity
								</h4>

								<MySentActivityComponent currentUser={currentUser} />
							</div>
						</Card>
					</Col>
				)}
			</Row>
			<ViewRewardModal
				isViewRewardModalVisible={isViewRewardModalVisible}
				handleCloseViewReward={handleCloseViewReward}
				rewardDetails={rewardDetails}
				isModalLoaderVisible={isModalLoaderVisible}
				isSharingVisible={isSharingVisible}
			/>
		</div>
	);
};

export default MyrewardsPresentation;
